import React from 'react'

import { useModelState } from '../../model-hooks/use-model-state'
import ChartLayout from '../../models/chart_layout'
import { AutoSaveState } from '../../models/constants'
import { getUuid } from '../../utils/helpers'
import { compareHlc, incrementHlc, initHlc, receiveHlc, serializeHlc } from '../../utils/hlc'
import { AutoSaveElement, DRAWINGS_SAVE_INTERVAL_MS, DRAWINGS_UPDATE_INTERVAL_MS } from './constants'
import { useDrawingAutoSaveApi } from './use-drawing-autosave-api'
import { useIsAutoSaveActive } from './use-is-auto-save-active'
import {
  getInitialStoreDrawings,
  getTickersAndIndicatorsInLayoutModel,
  mergeUpdatedDrawingsToInternalStore,
} from './utils'

function getNoteElementId(ticker: string) {
  return `note-${ticker}`
}

function getInitialNoteText(chartLayout: ChartLayout) {
  const { id, note = '' } = chartLayout.idea ?? {}
  return !id ? note : ''
}

export function useAutoSaveNote(chartLayout: ChartLayout) {
  const { fetchDrawings, saveDrawings } = useDrawingAutoSaveApi()
  const isAutoSaveActive = useIsAutoSaveActive(chartLayout)
  const activeChart = useModelState(chartLayout.activeChart, {
    watchProperties: ['quote'],
  })
  const activeTicker = activeChart?.quote()?.getRawTicker()

  const [activeNote, setActiveNote] = React.useState<string>(getInitialNoteText(chartLayout))
  const internalStoreRef = React.useRef<AutoSaveElement[]>([])
  const instanceUUIDRef = React.useRef(getUuid())
  const latestRemoteUpdateTimestampRef = React.useRef(initHlc(instanceUUIDRef.current, 0))
  const lastestLocalUpdateTimestampRef = React.useRef(initHlc(instanceUUIDRef.current, 0))
  const saveTimeoutRef = React.useRef<NodeJS.Timeout>()

  const updateActiveNote = React.useCallback(() => {
    const noteElement = internalStoreRef.current
      .filter(({ containerType, ticker }) => containerType === 'note' && ticker === activeTicker)
      .sort((a, b) => compareHlc(b.lastChange, a.lastChange))[0]

    let newNote = ''
    if (noteElement) {
      newNote = JSON.parse(noteElement.elementAttrs)?.text ?? ''
    }

    setActiveNote(newNote)
  }, [activeTicker])

  const saveLatestChanges = React.useCallback(
    (withDelayInMs = DRAWINGS_SAVE_INTERVAL_MS) => {
      chartLayout.setAutoSaveState(AutoSaveState.UnsavedNote)
      updateActiveNote()
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      saveTimeoutRef.current = setTimeout(async () => {
        chartLayout.setAutoSaveState(AutoSaveState.SavingNote)
        const result = await saveDrawings(
          internalStoreRef.current.filter(
            (item) =>
              compareHlc(item.lastChange, lastestLocalUpdateTimestampRef.current) > 0 &&
              item.lastChange.uuid === instanceUUIDRef.current
          )
        )
        chartLayout.setAutoSaveState(AutoSaveState.SavedNote)
        if (result) {
          lastestLocalUpdateTimestampRef.current = receiveHlc(lastestLocalUpdateTimestampRef.current, result)
        }
      }, withDelayInMs)
    },
    [saveDrawings, updateActiveNote, chartLayout]
  )

  const onNoteChange = React.useCallback(
    (note: string) => {
      if (!activeTicker) {
        return
      }

      const newNoteElement: AutoSaveElement = {
        elementId: getNoteElementId(activeTicker),
        zIndex: 0,
        ticker: activeTicker,
        lastChange: incrementHlc(lastestLocalUpdateTimestampRef.current, Date.now()),
        changeType: 'update',
        containerType: 'note',
        elementAttrs: JSON.stringify({ text: note }),
      }

      const hasUnchangedNote = internalStoreRef.current.some(
        (element) =>
          element.containerType === newNoteElement.containerType &&
          element.elementId === newNoteElement.elementId &&
          element.changeType === newNoteElement.changeType &&
          element.elementAttrs === newNoteElement.elementAttrs
      )

      if (!hasUnchangedNote) {
        internalStoreRef.current = [
          ...internalStoreRef.current.filter(
            ({ ticker, containerType }) => containerType !== 'note' && ticker !== activeTicker
          ),
          newNoteElement,
        ]
        saveLatestChanges()
      }
    },
    [saveLatestChanges, activeTicker]
  )

  const checkNotesUpdate = React.useCallback(
    async (isRefetchAll = false) => {
      const { tickers } = getTickersAndIndicatorsInLayoutModel(chartLayout)
      if (isRefetchAll) {
        latestRemoteUpdateTimestampRef.current = initHlc(instanceUUIDRef.current, 0)
      }

      const updatedDrawings = await fetchDrawings({
        tickers,
        containerTypes: ['note'],
        lastChange: serializeHlc(latestRemoteUpdateTimestampRef.current),
      })

      const { newLastUpdateTimestamp, newInternalStore } = mergeUpdatedDrawingsToInternalStore({
        updatedDrawings,
        currentInternalStore: internalStoreRef.current,
        lastUpdateTimestamp: latestRemoteUpdateTimestampRef.current,
      })

      internalStoreRef.current = newInternalStore
      latestRemoteUpdateTimestampRef.current = newLastUpdateTimestamp
      if (isRefetchAll) {
        lastestLocalUpdateTimestampRef.current = newLastUpdateTimestamp
      }

      const currentNoteElement = updatedDrawings.find(
        ({ ticker, elementId }) => ticker === activeTicker && elementId === getNoteElementId(activeTicker)
      )
      const currentNoteText = chartLayout.idea?.note

      if (currentNoteElement && isRefetchAll && currentNoteText) {
        const currentNoteElementText = JSON.parse(currentNoteElement.elementAttrs)?.text ?? ''
        const appendSavedNote =
          currentNoteElementText && currentNoteElementText !== currentNoteText ? `\r\n${currentNoteElementText}` : ''
        onNoteChange(`${currentNoteText}${appendSavedNote}`)
      } else {
        updateActiveNote()
      }
    },
    [chartLayout, activeTicker, fetchDrawings, onNoteChange, updateActiveNote]
  )

  React.useEffect(() => {
    // Restore initial drawings from server on page load
    const initialStoreDrawings = getInitialStoreDrawings()?.filter(({ containerType }) => containerType === 'note')
    if (initialStoreDrawings?.length) internalStoreRef.current = initialStoreDrawings

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  React.useEffect(() => {
    if (!isAutoSaveActive) {
      return
    }

    updateActiveNote()
    checkNotesUpdate(true)
    const checkDrawingUpdateInterval = setInterval(checkNotesUpdate, DRAWINGS_UPDATE_INTERVAL_MS)

    return () => {
      clearInterval(checkDrawingUpdateInterval)
    }
  }, [chartLayout, internalStoreRef, checkNotesUpdate, isAutoSaveActive, updateActiveNote])

  return {
    onNoteChange,
    activeNote,
    isAutoSaveActive,
  }
}
